import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';

import AzotteGrid from '../AzotteGrid';
import Modal from '../Modal';
import { useDialog } from '../DialogProvider';
import {
  getIndustryGroups, createIndustryGroup, updateIndustryGroup, deleteIndustryGroup,
  aiSweepStart, aiSweepStatus, aiSweepCancel, getIndustries,
  getAiProviders,
} from '../../services/api';

const DEFAULT_PROVIDER = 'gemini';

const emptyForm = { name: '', description: '', color: '' };

export default function IndustryGroupsTab() {
  const dialog = useDialog();
  const [groups, setGroups] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(null); // 'add' | group object | null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepStatus, setSweepStatus] = useState(null);
  const [sweepStarting, setSweepStarting] = useState(false);
  const [sweepMode, setSweepMode] = useState('ungrouped'); // 'ungrouped' | 'all'
  const pollRef = useRef(null);

  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState(DEFAULT_PROVIDER);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [g, i] = await Promise.all([getIndustryGroups(), getIndustries()]);
      setGroups(g.data || []);
      setIndustries(i.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load industry groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    let mounted = true;
    getAiProviders()
      .then((r) => {
        if (!mounted) return;
        const list = (r.data || []).filter((p) => p.isAvailable);
        setProviders(list);
        // Pick Gemini if available, else default flagged provider, else first.
        const gemini = list.find((p) => (p.name || '').toLowerCase() === DEFAULT_PROVIDER);
        const defaultProv = list.find((p) => p.isDefault);
        const pick = gemini || defaultProv || list[0];
        if (pick) setProvider(pick.name);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const ungroupedCount = useMemo(
    () => industries.filter((i) => !i.industryGroupId && i.industryGroupId !== 0).length,
    [industries],
  );

  const openAdd = () => { setForm(emptyForm); setModal('add'); };
  const openEdit = (g) => {
    setForm({ name: g.name || '', description: g.description || '', color: g.color || '' });
    setModal(g);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        color: form.color?.trim() || null,
      };
      if (modal === 'add') await createIndustryGroup(payload);
      else await updateIndustryGroup(modal.id, payload);
      setModal(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save industry group');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await dialog.confirm({
      title: 'Delete industry group',
      message: 'Industries in this group will become ungrouped. Continue?',
      confirmText: 'Delete',
      danger: true,
    }))) return;
    try {
      await deleteIndustryGroup(id);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete industry group');
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await aiSweepStatus(jobId);
        setSweepStatus(data);
        if (data?.status && data.status !== 'running') {
          stopPolling();
        }
      } catch {
        // transient — keep polling
      }
    }, 1500);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const openSweep = async (mode = 'ungrouped') => {
    if (mode === 'all') {
      const ok = await dialog.confirm({
        title: 'Reorganize all industries',
        message: `AI will re-evaluate every industry (${industries.length}) using "${provider}". Each match is applied immediately and leads with that industry are stamped with the resolved group ID.`,
        confirmText: 'Run',
      });
      if (!ok) return;
    }
    setSweepMode(mode);
    setSweepOpen(true);
    setSweepStatus(null);
    setSweepStarting(true);
    try {
      const { data } = await aiSweepStart({ provider, includeGrouped: mode === 'all' });
      setSweepStatus(data);
      if (data?.jobId && data.status === 'running') {
        startPolling(data.jobId);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start sweep');
      setSweepOpen(false);
    } finally {
      setSweepStarting(false);
    }
  };

  const handleCancelSweep = async () => {
    if (!sweepStatus?.jobId) return;
    try { await aiSweepCancel(sweepStatus.jobId); } catch { /* ignore */ }
  };

  const closeSweep = () => {
    stopPolling();
    setSweepOpen(false);
    setSweepStatus(null);
    fetchAll();
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (v, row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {row.color && (
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: row.color, border: '1px solid', borderColor: 'divider' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{v}</Typography>
        </Stack>
      ),
    },
    { key: 'description', header: 'Description', hideOnMobile: true, render: (v) => v || '-' },
    { key: 'industryCount', header: 'Industries', render: (v) => <Chip label={v ?? 0} size="small" /> },
    {
      key: 'actions', header: '', render: (_, row) => (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>Edit</Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => handleDelete(row.id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Group industries into higher-level buckets. Templates and segmentations target groups; the email renderer keeps using each lead&apos;s exact industry.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
        <TextField
          select
          size="small"
          label="AI Engine"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={providers.length === 0}
        >
          {providers.length === 0 && (
            <MenuItem value={provider}>{provider || 'No providers configured'}</MenuItem>
          )}
          {providers.map((p) => (
            <MenuItem key={p.name} value={p.name}>
              {p.displayName || p.name}{p.isDefault ? ' · default' : ''}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => openSweep('ungrouped')}
          disabled={loading || ungroupedCount === 0}
          title={ungroupedCount === 0 ? 'No ungrouped industries' : `AI-bucket ${ungroupedCount} ungrouped industries`}
        >
          Sweep ungrouped ({ungroupedCount})
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AutoFixHighIcon />}
          onClick={() => openSweep('all')}
          disabled={loading || industries.length === 0}
          title="Re-bucket every industry, including ones already assigned to a group"
        >
          Reorganize all ({industries.length})
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : groups.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>No industry groups defined</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>Add First Group</Button>
          </Box>
        </Paper>
      ) : (
        <AzotteGrid
          columns={columns}
          rows={groups}
          keyField="id"
          title="Industry Groups"
          actionColumnKey="actions"
          onAdd={openAdd}
        />
      )}

      {modal !== null && (
      <Modal
        onClose={() => setModal(null)}
        title={modal === 'add' ? 'Add industry group' : 'Edit industry group'}
        maxWidth="sm"
      >
        <form onSubmit={handleSave}>
          <Stack spacing={2}>
            <TextField
              label="Name"
              required
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              size="small"
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Description"
              multiline minRows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              size="small"
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              label="Color (hex)"
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder="#4F46E5"
              size="small"
              inputProps={{ maxLength: 9 }}
            />
          </Stack>
          <DialogActions sx={{ mt: 2, px: 0 }}>
            <Button onClick={() => setModal(null)} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Modal>
      )}

      <Dialog open={sweepOpen} onClose={() => sweepStatus?.status !== 'running' && closeSweep()} maxWidth="md" fullWidth>
        <DialogTitle>
          {sweepMode === 'all' ? 'AI reorganize — re-bucket every industry' : 'AI sweep — bucket ungrouped industries'}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Engine: {providers.find((p) => p.name === provider)?.displayName || provider}
            {sweepStatus?.jobId ? ` · job ${sweepStatus.jobId.slice(0, 8)}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(sweepStarting || !sweepStatus) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    {sweepStatus.processed} / {sweepStatus.total} processed
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" color="primary" label={`use ${sweepStatus.used || 0}`} />
                    <Chip size="small" color="success" label={`create ${sweepStatus.created || 0}`} />
                    <Chip size="small" variant="outlined" label={`skip ${sweepStatus.skipped || 0}`} />
                    <Chip size="small" color="info" label={`leads ${sweepStatus.leadsUpdated || 0}`} />
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={sweepStatus.total ? Math.min(100, (sweepStatus.processed / sweepStatus.total) * 100) : 0}
                />
              </Box>

              {sweepStatus.status === 'running' && sweepStatus.currentIndustryName && (
                <Alert severity="info" icon={<CircularProgress size={16} />}>
                  Processing: <strong>{sweepStatus.currentIndustryName}</strong> (id {sweepStatus.currentIndustryId})
                </Alert>
              )}
              {sweepStatus.status === 'done' && (
                <Alert severity="success">Sweep complete. {sweepStatus.leadsUpdated || 0} leads updated.</Alert>
              )}
              {sweepStatus.status === 'cancelled' && (
                <Alert severity="warning">Sweep cancelled at {sweepStatus.processed}/{sweepStatus.total}.</Alert>
              )}
              {sweepStatus.status === 'error' && (
                <Alert severity="error">{sweepStatus.error || 'Sweep failed.'}</Alert>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent results</Typography>
                {(sweepStatus.recent || []).length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No results yet.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Industry</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Conf.</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(sweepStatus.recent || []).map((r, idx) => (
                        <TableRow key={`${r.industryId}-${idx}`} hover>
                          <TableCell>{r.industryName}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.action}
                              color={r.action === 'use' ? 'primary' : r.action === 'create' ? 'success' : 'default'}
                              variant={r.action === 'skip' ? 'outlined' : 'filled'}
                            />
                          </TableCell>
                          <TableCell>
                            {r.action !== 'skip' && (r.groupName || r.newGroupName || (r.groupId ? `#${r.groupId}` : '—'))}
                            {r.action === 'skip' && '—'}
                          </TableCell>
                          <TableCell>{r.confidence != null ? r.confidence.toFixed(2) : '—'}</TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{r.reason || ''}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {sweepStatus?.status === 'running' ? (
            <Button color="warning" onClick={handleCancelSweep}>Cancel sweep</Button>
          ) : (
            <Button variant="contained" onClick={closeSweep}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
